import Vue from 'vue'
import { indexSettingHookFiles, readHookSpec, reportHookLoadFailure } from '@/business-logic/setting-hooks/settingHookDiscovery'

const NO_HOOKS = Object.freeze([])

/**
 * A hook source tells the mixin which components may be hooked and which hooks each one has right now.
 * `entriesFor(name)` returns `[{ setting, spec }]` in setting-key order; a lazy source returns a new array whenever
 * a hook file arrives, and reads it reactively, so computed values and renders that went through it update.
 */

// Every hook already loaded, e.g. from `buildSettingHooks`. Only the hooked functions are wrapped.
export const staticHookSource = (hooksByComponent) => ({
  componentNames: new Set(Object.keys(hooksByComponent)),
  entriesFor: (componentName) => hooksByComponent[componentName] || NO_HOOKS,
  lazy: false,
})

/**
 * Hook files loaded on demand: `load(path)` returns the module or a promise of it (a webpack lazy `require.context`).
 * Nothing loads until `loadSetting(key)`; the components that may be hooked are known from the file names alone.
 * Every function of those components is wrapped, so a hook that arrives later still applies to live instances.
 */
export const createLazyHookSource = ({ paths, load, registry }) => {
  const files = indexSettingHookFiles(paths, registry)
  const componentNames = new Set(files.map((file) => file.componentName))
  const loaded = Vue.observable(Object.fromEntries([...componentNames].map((name) => [name, NO_HOOKS])))
  const specs = new Map()
  const requested = new Set()

  const publish = (componentName) => {
    loaded[componentName] = Object.freeze(files
      .filter((file) => file.componentName === componentName && specs.has(file.path))
      .map((file) => Object.freeze({ setting: file.setting, spec: specs.get(file.path) })))
  }

  const loadFile = (file) => Promise.resolve()
    .then(() => load(file.path))
    .then((module) => {
      const spec = readHookSpec(file.path, module)
      if (spec) {
        specs.set(file.path, spec)
        publish(file.componentName)
      }
      return spec
    })
    .catch((error) => {
      // Asked again on the next change of the setting, e.g. after a dropped connection or a new deploy.
      requested.delete(file.path)
      reportHookLoadFailure(file.path, error)
    })

  return {
    componentNames,
    entriesFor: (componentName) => loaded[componentName] || NO_HOOKS,
    lazy: true,
    settings: [...new Map(files.map((file) => [file.setting.key, file.setting])).values()],
    // Loads the setting's hook files once; resolves when they are in place.
    loadSetting(key) {
      const pending = files.filter((file) => file.setting.key === key && !requested.has(file.path))
      pending.forEach((file) => requested.add(file.path))
      return Promise.all(pending.map(loadFile))
    },
  }
}
